import { useEffect, useMemo, useState } from 'react';
import { VOUCHERS } from '@/data/vouchers';
import type { VoucherEntry } from '@/data/vouchers';
import { FlagIcon, hasFlagIcon } from '@/components/FlagIcon';

type SortKey = 'price' | 'country';
type RegionFilter = 'all' | VoucherEntry['region'];
type LangFilter = 'all' | VoucherEntry['language'];

export function VoucherTable() {
  const [sortKey, setSortKey] = useState<SortKey>('price');
  const [region, setRegion] = useState<RegionFilter>('all');
  const [lang, setLang] = useState<LangFilter>('all');
  const [isWide, setIsWide] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= 720 : true,
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setIsWide(window.innerWidth >= 720);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const filtered = useMemo<VoucherEntry[]>(() => {
    return VOUCHERS.filter((v) => (region === 'all' || v.region === region) && (lang === 'all' || v.language === lang)).sort(
      (a, b) => {
        if (sortKey === 'price') return a.priceEur - b.priceEur;
        return a.country.localeCompare(b.country);
      },
    );
  }, [region, lang, sortKey]);

  const regions: ReadonlyArray<{ id: RegionFilter; label: string }> = [
    { id: 'all', label: 'Все регионы' },
    { id: 'TR', label: '🇹🇷 Türkiye' },
    { id: 'CIS', label: 'СНГ' },
    { id: 'CAUC', label: 'Кавказ' },
    { id: 'EU', label: '🇪🇺 EU' },
  ];
  const langs: ReadonlyArray<{ id: LangFilter; label: string }> = [
    { id: 'all', label: 'Все языки' },
    { id: 'RU', label: 'RU' },
    { id: 'EN', label: 'EN' },
    { id: 'TR', label: 'TR' },
    { id: 'PL', label: 'PL' },
    { id: 'DE', label: 'DE' },
  ];

  return (
    <div
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-lg)',
        padding: '18px 20px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: 14,
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <h3
          style={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            fontSize: 15,
            color: 'var(--ink)',
            margin: 0,
          }}
        >
          🎫 CTFL Voucher · {filtered.length} стран
        </h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={() => setSortKey(sortKey === 'price' ? 'country' : 'price')}
            style={{
              fontFamily: 'Space Mono, monospace',
              fontSize: 11,
              padding: '4px 10px',
              borderRadius: 50,
              background: 'var(--surface-2)',
              border: '1px solid var(--border)',
              color: 'var(--ink-soft)',
              cursor: 'pointer',
            }}
          >
            Sort: {sortKey === 'price' ? 'price ↑' : 'country A→Z'}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {regions.map((r) => (
          <Chip key={r.id} active={region === r.id} onClick={() => setRegion(r.id)}>
            {r.label}
          </Chip>
        ))}
        <span style={{ width: 1, background: 'var(--border-strong)', margin: '0 6px' }} />
        {langs.map((l) => (
          <Chip key={l.id} active={lang === l.id} onClick={() => setLang(l.id)}>
            {l.label}
          </Chip>
        ))}
      </div>

      {isWide ? <Table entries={filtered} /> : <Cards entries={filtered} />}

      <div
        style={{
          marginTop: 12,
          paddingTop: 10,
          fontFamily: 'Space Mono, monospace',
          fontSize: 10,
          color: 'var(--ink-mute)',
          borderTop: '1px dashed var(--border)',
        }}
      >
        Цены в EUR · ориентировочные · обновлять ежегодно. Для точной цены проходи на сайт board'а.
      </div>
    </div>
  );
}

function Chip({ active, children, onClick }: { active: boolean; children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        fontFamily: 'Inter, sans-serif',
        fontSize: 11.5,
        fontWeight: 600,
        padding: '4px 10px',
        borderRadius: 50,
        background: active ? 'var(--ink)' : 'var(--surface-2)',
        color: active ? 'var(--bg)' : 'var(--ink-soft)',
        border: `1px solid ${active ? 'var(--ink)' : 'var(--border)'}`,
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

function Table({ entries }: { entries: VoucherEntry[] }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Inter, sans-serif', fontSize: 13 }}>
      <thead>
        <tr style={{ textAlign: 'left' }}>
          <th style={th}>Страна</th>
          <th style={th}>Язык</th>
          <th style={th}>Формат</th>
          <th style={{ ...th, textAlign: 'right' }}>Цена (EUR)</th>
          <th style={th}>Локально</th>
          <th style={th}>Заметки</th>
          <th style={th}> </th>
        </tr>
      </thead>
      <tbody>
        {entries.map((v) => (
          <tr key={v.id}>
            <td style={td}>
              {hasFlagIcon(v.id) ? <FlagIcon code={v.id} size={20} /> : v.flag} {v.country}
            </td>
            <td style={td}>{v.language}</td>
            <td style={td}>
              {v.format === 'both' ? 'paper · online' : v.format}
            </td>
            <td style={{ ...td, textAlign: 'right', fontFamily: 'Space Mono, monospace', fontWeight: 700 }}>
              {v.priceEur} €
            </td>
            <td style={{ ...td, fontFamily: 'Space Mono, monospace', color: 'var(--ink-soft)' }}>{v.localPrice}</td>
            <td style={{ ...td, fontSize: 11.5, color: 'var(--ink-soft)' }}>{v.notes ?? '—'}</td>
            <td style={td}>
              <a
                href={v.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: 11, color: 'var(--accent-deep)', textDecoration: 'none' }}
              >
                сайт →
              </a>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Cards({ entries }: { entries: VoucherEntry[] }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8 }}>
      {entries.map((v) => (
        <article
          key={v.id}
          style={{
            background: 'var(--surface-2)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-md)',
            padding: '10px 12px',
            fontFamily: 'Inter, sans-serif',
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)' }}>
            {hasFlagIcon(v.id) ? <FlagIcon code={v.id} size={20} /> : v.flag} {v.country}
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-soft)', marginTop: 2 }}>
            {v.language} · {v.format === 'both' ? 'paper/online' : v.format}
          </div>
          <div
            style={{
              fontFamily: 'Space Mono, monospace',
              fontWeight: 700,
              fontSize: 14,
              color: 'var(--ink)',
              marginTop: 6,
            }}
          >
            {v.priceEur} €
          </div>
          <div style={{ fontFamily: 'Space Mono, monospace', fontSize: 10, color: 'var(--ink-soft)' }}>{v.localPrice}</div>
          <a
            href={v.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 11, color: 'var(--accent-deep)', display: 'inline-block', marginTop: 4 }}
          >
            сайт →
          </a>
        </article>
      ))}
    </div>
  );
}

const th: React.CSSProperties = {
  borderBottom: '1px solid var(--border-strong)',
  padding: '6px 10px',
  fontFamily: 'Space Mono, monospace',
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: 'var(--ink-mute)',
  fontWeight: 700,
};

const td: React.CSSProperties = {
  padding: '8px 10px',
  borderBottom: '1px solid var(--border)',
  color: 'var(--ink)',
};

